/**
 * This component is used to edit an entity property
 * in FloatingEntityEditorCell.
 *
 * It is a very similar component to EditableCell, but it has special logic
 * to handle values of parent columns, because floating entity editor
 * is not a cell in a column but just "object" editor for the value in the dataset cell
 */
import { Column } from "@tanstack/react-table";

import { EnumOptionsBE } from "src/api/flowTypes";
import {
  DatasetRow,
  DesiredType,
  DesiredTypes,
  DesiredTypeValues,
} from "src/api/types";
import { PropertyDefinitionOutputTypeEnum } from "src/clients/features-control";
import { JSONValue } from "src/datasets/DatasetTable/types";
import {
  jsonToPon,
  parseJsonOrReturnAsIs,
  ponToJson,
} from "src/datasets/DatasetTable/utils";
import { CellWrapper } from "src/datasets/components/CellWrapper";
import { DefaultInputCell } from "src/datasets/components/DefaultInputCell";
import { EnumPicker } from "src/datasets/components/EnumPicker";
import { JSONEditorCell } from "src/datasets/components/JSONEditorCell";
import { JSONPill } from "src/datasets/components/JSONPill";
import { RelationCellDropdown } from "src/datasets/components/RelationCellDropdown";
import { RelationPill } from "src/datasets/components/RelationJSONEditor";
import { getErrorDetails } from "src/datasets/components/errorUtils";
import { useCellLocalValue } from "src/datasets/components/useCellLocalValue";
import { getSelectedValues } from "src/datasets/utils";
import { Cardinality, EntitySchemaProperty } from "src/entities/queries";

export const EntityPropertyEditorCell: React.FC<{
  property: EntitySchemaProperty;
  propertyKey: string;
  onChange: (propertyName: string, value: JSONValue) => void;
  onGoToRelation: (relation: string, path: string) => void;
  disabled: boolean;
  value: JSONValue;
  editing: boolean;
  selected: boolean;
  onBlur: () => void;
  enumValues: EnumOptionsBE | undefined;
}> = ({
  onChange,
  onGoToRelation,
  disabled,
  value: propValue,
  editing,
  onBlur,
  selected,
  property,
  propertyKey,
  enumValues,
}) => {
  const desiredType = getDesiredTypeFromPropertyType(
    property._type,
    property._cardinality,
  );
  const { value, error, setError, validateAndSave, setLocalValue } =
    useCellLocalValue(propValue, (value) => onChange(propertyKey, value), {
      desiredType,
      nullable: true,
      required: false,
      enumValues,
    });

  const handleChange = (value: string) => {
    onBlur();
    validateAndSave(value);
  };

  const isJsonEditorCell = ["object", "array", "any"].includes(desiredType);

  const isRelationCell =
    property._type === "relation" && property._cardinality === "1";

  if (isRelationCell && property._rel_schema) {
    const relationSchema = property._rel_schema;
    return propValue ? (
      <div className="flex h-full items-center px-1.5">
        <RelationPill
          fallback={<JSONPill type="string" value={value} />}
          schema={relationSchema}
          value={propValue}
          onClick={() => onGoToRelation(relationSchema, propertyKey)}
          onDelete={() => onChange(propertyKey, null)}
        />
      </div>
    ) : (
      <CellWrapper
        disabled={disabled}
        editing={editing}
        error={getErrorDetails(error, value, (newValue) =>
          validateAndSave(newValue),
        )}
        invalid={false}
        selected={selected}
        warning={false}
      >
        <RelationCellDropdown
          depth={1}
          isOpen={editing}
          relation={relationSchema}
          onFill={(newValue) => validateAndSave(newValue)}
        />
      </CellWrapper>
    );
  }

  if (isJsonEditorCell) {
    return (
      <JSONEditorCell
        column={{ columnDef: { meta: undefined } } as Column<DatasetRow>}
        disabled={disabled}
        editing={editing}
        invalid={!!(error && value)}
        isOpen={editing}
        postfix={
          !!enumValues?.length && (
            <EnumPicker
              options={enumValues}
              selectedValues={getSelectedValues(propValue)}
              onSelect={(selectedValue) => {
                // Handle multiselect for tags
                if (desiredType === "array") {
                  const parsedCellValue = parseJsonOrReturnAsIs(
                    ponToJson(value),
                  );
                  const valueToAdd = JSON.parse(selectedValue);
                  if (Array.isArray(parsedCellValue)) {
                    const updatedValue = parsedCellValue.includes(valueToAdd)
                      ? parsedCellValue.filter((item) => item !== valueToAdd)
                      : [...parsedCellValue, valueToAdd];
                    const newValue = jsonToPon(JSON.stringify(updatedValue));

                    handleChange(newValue);
                  } else {
                    const newValue = jsonToPon(JSON.stringify([valueToAdd]));
                    handleChange(newValue);
                  }
                  return;
                }

                handleChange(selectedValue);
              }}
            />
          )
        }
        rowIndex={1}
        selected={selected}
        type={Array.isArray(propValue) ? "array" : typeof propValue}
        value={value}
        warning={false}
        onChange={handleChange}
        onClose={onBlur}
      />
    );
  }

  return (
    <DefaultInputCell
      desiredType={desiredType}
      disabled={disabled}
      editing={editing}
      error={error}
      postfix={
        !!enumValues?.length && (
          <EnumPicker
            options={enumValues}
            selectedValues={getSelectedValues(propValue)}
            onSelect={(selectedValue) => {
              // Handle multiselect for tags
              if (desiredType === "array") {
                const parsedCellValue = parseJsonOrReturnAsIs(ponToJson(value));
                const valueToAdd = JSON.parse(selectedValue);
                if (Array.isArray(parsedCellValue)) {
                  const updatedValue = parsedCellValue.includes(valueToAdd)
                    ? parsedCellValue.filter((item) => item !== valueToAdd)
                    : [...parsedCellValue, valueToAdd];
                  const newValue = jsonToPon(JSON.stringify(updatedValue));

                  handleChange(newValue);
                } else {
                  const newValue = jsonToPon(JSON.stringify([valueToAdd]));
                  handleChange(newValue);
                }
                return;
              }

              handleChange(selectedValue);
            }}
          />
        )
      }
      selected={selected}
      value={value}
      // No warning for subfields
      warning={false}
      onChange={handleChange}
      onResetError={() => setError(undefined)}
      onSetLocalValue={setLocalValue}
    />
  );
};

const getDesiredTypeFromPropertyType = (
  propertyType: string,
  propertyCardinality: Cardinality | undefined,
): DesiredType => {
  if (DesiredTypes.includes(propertyType as DesiredType)) {
    return propertyType as DesiredType;
  }

  if (
    propertyType === PropertyDefinitionOutputTypeEnum.ARRAY ||
    propertyType === PropertyDefinitionOutputTypeEnum.TAGS
  ) {
    return DesiredTypeValues.array;
  }

  if (
    propertyType === PropertyDefinitionOutputTypeEnum.RELATION &&
    propertyCardinality === "1-to-many"
  ) {
    return DesiredTypeValues.array;
  }

  return DesiredTypeValues.object;
};
